import { useState } from 'react';
import type {
  AdminFeedbackType,
  FeedbackSeverity,
  CreateFeedbackRequest,
} from '../../types/feedback';
import { FEEDBACK_TYPE_LABELS } from '../../utils/feedback-labels';
import { SEVERITY_LABELS } from '../../utils/severity-labels';

type FeedbackModalProps = {
  preselectedType: AdminFeedbackType;
  conversationId: string;
  messageId: string;
  userMessage: string;
  assistantAnswer: string;
  onSubmit: (data: CreateFeedbackRequest) => Promise<void>;
  onClose: () => void;
};

export const FeedbackModal = ({
  preselectedType,
  conversationId,
  messageId,
  userMessage,
  assistantAnswer,
  onSubmit,
  onClose,
}: FeedbackModalProps) => {
  const [type, setType] = useState<AdminFeedbackType>(preselectedType);
  const [severity, setSeverity] = useState<FeedbackSeverity>('medium');
  const [correctedAnswer, setCorrectedAnswer] = useState('');
  const [expectedIntent, setExpectedIntent] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        conversationId,
        messageId,
        userMessage,
        assistantAnswer,
        type,
        severity,
        correctedAnswer: correctedAnswer.trim() || undefined,
        expectedIntent: expectedIntent.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte spara feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="mx-4 w-full max-w-lg rounded-xl border border-black/10 bg-white shadow-2xl">
        <div className="border-b border-black/10 px-6 py-4">
          <h3 className="text-lg font-medium text-brand-blue">Admin Feedback</h3>
          <p className="mt-1 text-xs text-text-grey">Ge detaljerad feedback på assistants svar.</p>
        </div>

        <div className="space-y-4 px-6 py-4 max-h-[70vh] overflow-y-auto">
          <div className="rounded-md bg-brand-beige-tint-1 p-3 text-xs text-brand-blue">
            <p className="font-medium mb-1">Användarens fråga:</p>
            <p className="text-text-grey">{userMessage}</p>
          </div>

          <div className="rounded-md bg-brand-beige-tint-1 p-3 text-xs text-brand-blue">
            <p className="font-medium mb-1">Assistants svar:</p>
            <p className="text-text-grey">{assistantAnswer}</p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-text-grey mb-1">
              Feedback-typ
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as AdminFeedbackType)}
              className="w-full rounded-sm border border-black/10 bg-white px-3 py-2 text-sm text-brand-blue focus:border-brand-blue focus:outline-none"
            >
              {Object.entries(FEEDBACK_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-text-grey mb-1">
              Allvarlighetsgrad
            </label>
            <div className="flex gap-3">
              {(['low', 'medium', 'high'] as const).map((s) => (
                <label key={s} className="flex items-center gap-2 text-sm text-brand-blue">
                  <input
                    type="radio"
                    name="severity"
                    value={s}
                    checked={severity === s}
                    onChange={() => setSeverity(s)}
                    className="text-brand-blue focus:ring-brand-blue"
                  />
                  {SEVERITY_LABELS[s]}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-text-grey mb-1">
              Korrekt svar
            </label>
            <textarea
              value={correctedAnswer}
              onChange={(e) => setCorrectedAnswer(e.target.value)}
              placeholder="Vad borde agenten ha svarat?"
              rows={3}
              className="w-full rounded-sm border border-black/10 bg-white px-3 py-2 text-sm text-brand-blue placeholder:text-text-grey focus:border-brand-blue focus:outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-text-grey mb-1">
              Förväntad intent
            </label>
            <input
              type="text"
              value={expectedIntent}
              onChange={(e) => setExpectedIntent(e.target.value)}
              placeholder="t.ex. returns, order_status"
              className="w-full rounded-sm border border-black/10 bg-white px-3 py-2 text-sm text-brand-blue placeholder:text-text-grey focus:border-brand-blue focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-text-grey mb-1">
              Intern notering
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anteckningar för utvecklarna..."
              rows={2}
              className="w-full rounded-sm border border-black/10 bg-white px-3 py-2 text-sm text-brand-blue placeholder:text-text-grey focus:border-brand-blue focus:outline-none resize-none"
            />
          </div>

          {error ? (
            <div className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-3 border-t border-black/10 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-sm border border-black/10 bg-white px-4 py-2 text-sm text-text-grey transition-colors hover:border-brand-blue hover:text-brand-blue disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-sm border border-brand-blue bg-brand-blue px-4 py-2 text-sm text-white transition-colors hover:bg-white hover:text-brand-blue disabled:opacity-50"
          >
            {submitting ? 'Sparar...' : 'Spara feedback'}
          </button>
        </div>
      </div>
    </div>
  );
};
